import { apiClient } from '@/lib/api'
import type { Passkey, SessionUser } from '@/types/auth'

export function login(username: string, password: string) {
  return apiClient<SessionUser>('/auth/login', { method: 'POST', body: { username, password } })
}

export function logout() {
  return apiClient<void>('/auth/logout', { method: 'POST' })
}

export function fetchMe() {
  return apiClient<SessionUser>('/auth/me')
}

// --- password management ---

// current_password is omitted when the identity has no password yet
// (passkey-only) and is setting an initial one.
export function changePassword(currentPassword: string, newPassword: string) {
  return apiClient<SessionUser>('/auth/password', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  })
}

// Always resolves (202) — the backend never reveals whether the login exists.
export function requestPasswordReset(login: string) {
  return apiClient<{ sent: boolean }>('/auth/password-reset/request', {
    method: 'POST',
    body: { login },
  })
}

export function confirmPasswordReset(token: string, newPassword: string) {
  return apiClient<SessionUser>('/auth/password-reset/confirm', {
    method: 'POST',
    body: { token, new_password: newPassword },
  })
}

// --- passkeys ---

export interface CeremonyBeginResponse {
  ceremony_id: string
  // Browser ceremony options (PublicKeyCredentialCreationOptions /
  // PublicKeyCredentialRequestOptions). Typed as any: @github/webauthn-json
  // consumes them opaquely.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any
}

export function passkeyLoginBegin(username?: string) {
  return apiClient<CeremonyBeginResponse>('/auth/passkey/login/begin', {
    method: 'POST',
    body: username ? { username } : {},
  })
}

export function passkeyLoginFinish(ceremonyId: string, response: unknown) {
  return apiClient<SessionUser>('/auth/passkey/login/finish', {
    method: 'POST',
    body: { ceremony_id: ceremonyId, response },
  })
}

export function passkeyRegisterBegin() {
  return apiClient<CeremonyBeginResponse>('/auth/passkeys/register/begin', { method: 'POST' })
}

export function passkeyRegisterFinish(ceremonyId: string, name: string, response: unknown) {
  return apiClient<Passkey>('/auth/passkeys/register/finish', {
    method: 'POST',
    body: { ceremony_id: ceremonyId, name, response },
  })
}

export function fetchPasskeys() {
  return apiClient<Passkey[]>('/auth/passkeys')
}

export function deletePasskey(id: string) {
  return apiClient<void>(`/auth/passkeys/${id}`, { method: 'DELETE' })
}
